import React, { useState, useEffect, useMemo, useCallback } from "react";
import { colors, font, displayName } from "../utils/theme.js";
import { api } from "../api/client.js";
import ClientPage from "./ClientPage.jsx";
import { buildRoster, progressPct, fmtDate, projectTitle, todayOf } from "./clientLogic.js";
import TierBadge from "./TierBadge.jsx";

const readHashParam = (key) => { try { const m = new RegExp(`[#&]${key}=([^&]+)`).exec(window.location.hash); return m ? decodeURIComponent(m[1]) : null; } catch { return null; } };
const clientHash = (clientId, projectId) => "client=" + encodeURIComponent(clientId) + (projectId ? "&project=" + encodeURIComponent(projectId) : "");

// Clients feature (increment 5, option B). Overview = PM roster + aggregate cards; clicking a
// card opens a dedicated full-page client view (not a modal). NOT a create surface — clients
// are born only in the Ops Planner add-project flow.
export default function ClientsPage({ user, users }) {
  const [summaries, setSummaries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState(null);
  // Selected client (and project) is driven by the URL hash (#client=<id>&project=<id>) so a
  // client page is a shareable link and the browser Back/Forward buttons work for free.
  const [selectedId, setSelectedId] = useState(() => readHashParam("client"));
  const [selectedProjectId, setSelectedProjectId] = useState(() => readHashParam("project"));
  const [selectedTaskId, setSelectedTaskId] = useState(() => readHashParam("task")); // deep link from the daily email
  const [selectedCrmTrackId, setSelectedCrmTrackId] = useState(() => readHashParam("crm")); // CRM follow-up deep link
  const [mineOnly, setMineOnly] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [sum, ops, reg] = await Promise.all([api.clients.summary(), api.opsplanner.list(), api.clients.list()]);
      setSummaries(Array.isArray(sum) ? sum : []);
      setProjects(Array.isArray(ops) ? ops : []);
      setClients(Array.isArray(reg) ? reg : []);
    } catch (e) {
      setError(e.message || "Could not load clients.");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onHash = () => { setSelectedId(readHashParam("client")); setSelectedProjectId(readHashParam("project")); setSelectedTaskId(readHashParam("task")); setSelectedCrmTrackId(readHashParam("crm")); };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const openClient = (id, projectId) => { window.location.hash = clientHash(id, projectId); };
  // Switching project inside a client page replaces the history entry, so Back still returns
  // to the Clients overview rather than stepping through every project viewed.
  const selectProject = (projectId) => {
    try { window.history.replaceState(null, "", "#" + clientHash(selectedId, projectId)); } catch { /* ignore */ }
    setSelectedProjectId(projectId);
  };
  const back = () => { window.history.back(); };

  const userName = useCallback((id) => { if (!id) return "—"; const u = (users || []).find((x) => x.id === id || x.email === id); return u ? displayName(u) : id; }, [users]);
  const clientNameOf = useCallback((p) => { const c = clients.find((x) => x.id === p.clientId); return c ? c.name : (p.client || "—"); }, [clients]);

  const roster = useMemo(() => buildRoster(projects)
    .map((g) => ({ ...g, pmName: g.pmId === "__unassigned__" ? "Unassigned" : userName(g.pmId) }))
    .sort((a, b) => (a.pmId === "__unassigned__" ? 1 : b.pmId === "__unassigned__" ? -1 : a.pmName.localeCompare(b.pmName))), [projects, userName]);

  // One card per client — or, when a client has two or more live projects, one card per
  // project named "Client - Project" with that project's own dates, people and counts.
  const cards = useMemo(() => summaries.flatMap((s) => (s.projects?.length > 1
    ? s.projects.map((p) => ({ key: `${s.id}:${p.id}`, s, name: `${s.name} - ${p.title}`, project: p, projectId: p.id, counts: p }))
    : [{ key: s.id, s, name: s.name, project: s.currentProject, projectId: null, counts: s }])), [summaries]);

  // "My projects": cards where the signed-in user is PM or Account Owner (fields may hold a
  // user id or an email). A single-project card matches on any of the client's live projects;
  // a per-project card only on its own project.
  const myClientIds = useMemo(() => {
    const me = new Set([user?.id, user?.email].filter(Boolean));
    return new Set(projects
      .filter((p) => !p.archivedAt && p.clientId &&
        [p.projectManager, p.accountOwner ?? p.projectOversight].some((v) => v && me.has(v)))
      .map((p) => p.clientId));
  }, [projects, user]);
  const isMine = useCallback((c) => {
    if (!c.projectId) return myClientIds.has(c.s.id);
    const me = new Set([user?.id, user?.email].filter(Boolean));
    return [c.project.projectManager, c.project.accountOwner].some((v) => v && me.has(v));
  }, [myClientIds, user]);
  const visibleCards = mineOnly ? cards.filter(isMine) : cards;
  const byName = (a, b) => a.name.localeCompare(b.name);
  const activeCards = visibleCards.filter((c) => !c.s.archived).sort(byName);
  const archivedCards = visibleCards.filter((c) => c.s.archived).sort(byName);

  if (loading) return <div style={{ padding: 40, color: colors.textMuted }}>Loading clients…</div>;
  if (error) return (
    <div style={{ padding: 40 }}>
      <div style={{ color: colors.danger, marginBottom: 12 }}>Couldn’t load clients: {error}</div>
      <button onClick={load} style={btn(colors.accent)}>Retry</button>
    </div>
  );

  if (selectedId) {
    return (
      <ClientPage clientId={selectedId} user={user} users={users}
        projects={projects.filter((p) => p.clientId === selectedId)}
        selectedProjectId={selectedProjectId} onSelectProject={selectProject} initialTaskId={selectedTaskId} initialCrmTrackId={selectedCrmTrackId}
        onBack={back} onChanged={load} />
    );
  }

  return (
    <div>
      <h1 style={{ margin: "0 0 16px", fontSize: 22, fontWeight: 700, color: colors.text, letterSpacing: -0.3 }}>Clients</h1>

      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 20, marginBottom: 24 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: colors.textDim, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>Project Manager Roster</div>
        {roster.length === 0 ? (
          <div style={{ color: colors.textDim, fontSize: 13 }}>No upcoming, active, or recently-closed projects.</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 16 }}>
            {roster.map((g) => (
              <div key={g.pmId}>
                <div style={{ fontSize: 13, fontWeight: 700, color: colors.text, marginBottom: 6 }}>{g.pmName}</div>
                {g.items.map(({ project, clientId, label, multi }) => (
                  <div key={project.id} style={{ fontSize: 12, color: colors.textMuted, padding: "2px 0", display: "flex", alignItems: "center", gap: 6 }}>
                    <TierBadge tier={clients.find((c) => c.id === clientId)?.tier} />
                    <span>{clientNameOf(project)}{multi ? ` - ${projectTitle(project)}` : ""}{label ? <span style={{ color: label === "closed" ? colors.danger : colors.success }}> ({label})</span> : null}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <button onClick={() => setMineOnly((v) => !v)} aria-pressed={mineOnly} style={{
          padding: "7px 14px", borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: font,
          border: `1px solid ${mineOnly ? colors.accent : colors.border}`,
          background: mineOnly ? colors.accentDim : colors.surface,
          color: mineOnly ? colors.accent : colors.textMuted,
        }}>My projects{mineOnly ? ` (${visibleCards.length})` : ""}</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
        {visibleCards.length === 0 ? (
          <div style={{ color: colors.textDim, fontSize: 13 }}>{mineOnly ? "You’re not listed as Project Manager or Account Owner on any client." : "No clients yet — add one from Ops Planner."}</div>
        ) : (
          <>
            {activeCards.map((c) => <ClientCard key={c.key} card={c} userName={userName} onOpen={() => openClient(c.s.id, c.projectId)} />)}
            {/* Archived clients fold into one card at the end; opening it lists them below. */}
            {archivedCards.length > 0 && (
              <button type="button" onClick={() => setShowArchived((v) => !v)} aria-expanded={showArchived}
                style={{ background: colors.surface, border: `1px dashed ${colors.borderLight}`, borderRadius: 12, padding: 16, cursor: "pointer", textAlign: "left", fontFamily: font, display: "flex", alignItems: "center", gap: 10, minHeight: 80 }}>
                <span style={{ fontSize: 13, color: colors.textDim }}>{showArchived ? "▾" : "▸"}</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: colors.textMuted }}>Archived projects</span>
                <span style={{ fontSize: 12, color: colors.textDim }}>({archivedCards.length})</span>
              </button>
            )}
          </>
        )}
      </div>
      {showArchived && archivedCards.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16, marginTop: 16 }}>
          {archivedCards.map((c) => <ClientCard key={c.key} card={c} userName={userName} onOpen={() => openClient(c.s.id, c.projectId)} />)}
        </div>
      )}
    </div>
  );
}

function ClientCard({ card, userName, onOpen }) {
  const { s, name, counts, projectId } = card;
  const cp = card.project;
  const upcoming = cp?.startDate && cp.startDate > todayOf();
  const pct = cp ? progressPct(cp.startDate, cp.endDate) : null;
  return (
    <div onClick={onOpen} style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 16, cursor: "pointer", opacity: s.archived ? 0.72 : 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <TierBadge tier={s.tier} />
        <span style={{ fontSize: 15, fontWeight: 700, color: colors.text }}>{name}</span>
        {upcoming && <span style={{ fontSize: 10, fontWeight: 700, color: colors.success, border: `1px solid ${colors.success}`, borderRadius: 4, padding: "1px 6px" }}>UPCOMING</span>}
        {s.archived && <span style={{ fontSize: 10, fontWeight: 700, color: colors.textDim, border: `1px solid ${colors.border}`, borderRadius: 4, padding: "1px 6px" }}>ARCHIVED</span>}
      </div>
      {cp ? (
        <>
          {!projectId && <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 8 }}>{cp.title}</div>}
          {pct != null && <div style={{ height: 5, background: colors.bg, borderRadius: 3, overflow: "hidden", marginBottom: 8 }}><div style={{ width: `${pct}%`, height: "100%", background: colors.accent }} /></div>}
          <div style={{ fontSize: 11, color: colors.textDim, marginBottom: 8 }}>{fmtDate(cp.startDate)} – {fmtDate(cp.endDate)} · Owner {userName(cp.accountOwner)} · PM {userName(cp.projectManager)}</div>
        </>
      ) : <div style={{ fontSize: 12, color: colors.textDim, marginBottom: 8 }}>No active project</div>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, fontSize: 12 }}>
        <span style={{ color: colors.textMuted }}>Objectives {counts.objectiveProgress.done} of {counts.objectiveProgress.total}</span>
        <span style={{ color: colors.textMuted }}>{counts.openTasks} open{counts.pastDueTasks > 0 && <span style={{ color: colors.danger, fontWeight: 700 }}> · {counts.pastDueTasks} past due</span>}</span>
      </div>
      <div style={{ fontSize: 11, color: colors.textDim, marginTop: 8 }}>{s.latestNoteAt ? `Last note ${fmtDate(s.latestNoteAt.slice(0, 10))}` : "No notes yet"}</div>
    </div>
  );
}

function btn(bg) {
  return { padding: "9px 18px", background: bg, border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: font };
}
